import React from 'react';
import GenericModal from '../../../../ui-components/Modal/GenericModal';
import { GenericTag } from '../../../../ui-components/Tag/GenericTag';
import { SkeletonLine } from '../../../../ui-components/Skeleton/GenericSkeleton';
import { RouteTableModalProps } from './BGPModalsData';
import { CprEntry, TagAttribution, routeFamilyLabel } from '../BgpVisibilityData';
import { formatTagAttribution } from '../../../helpers/bgpValidation';
import { prefixIdentityKey } from '../utils/routeModel';

/**
 * The tag attribution for this destination, if any (CTX-129). Every prefix reaching a given
 * route table / subnet was matched by the same rule, so the first attribution found describes
 * the destination — scanning the rest would just re-read the same value.
 *
 * `null` means either "pinned explicitly in the rule" or "document predates the field"; the
 * payload can't tell those apart, so the caller renders nothing rather than guessing "Manual".
 */
const findTagAttribution = (prefixes: CprEntry[]): TagAttribution | null =>
  prefixes.find((prefix) => prefix.tag_attribution)?.tag_attribution ?? null;

/** One prefix and the single path the route table actually installs for it. */
interface InboundPrefix {
  key: string;
  prefix: string;
  family: CprEntry['family'];
  /**
   * The winning path (`selected`). Falls back to the first entry when the payload marks none,
   * so a document without a decision still renders a route rather than nothing.
   */
  route: CprEntry;
}

/**
 * Reduces the inbound entries to **one row per destination** — what a route table actually is
 * (CTX-322).
 *
 * The route model expands one entry per BGP candidate path, so `0.0.0.0/0` learned from three
 * neighbors arrived here as three entries. Rendering all three implied the route table had
 * installed three routes for one destination, which no route table ever does: AWS shows exactly
 * one Target per Destination, and only the `selected` path is programmed. The losing candidates
 * are BGP decision context, not route-table contents, and they remain visible in **Table** view
 * where each prefix expands into its per-path breakdown with Best / Alt badges.
 *
 * Outbound deliberately keeps its flat, per-neighbor list: there the cards *are* the neighbors
 * the subnet is announced to, not competing paths to one destination.
 */
const groupInboundPrefixes = (prefixes: CprEntry[]): InboundPrefix[] => {
  const pathsByPrefix = new Map<string, CprEntry[]>();
  for (const entry of prefixes) {
    const key = prefixIdentityKey(entry);
    const existing = pathsByPrefix.get(key);
    if (existing) {
      existing.push(entry);
    } else {
      pathsByPrefix.set(key, [entry]);
    }
  }
  return Array.from(pathsByPrefix.entries()).map(([key, paths]) => ({
    key,
    prefix: paths[0].prefix,
    family: paths[0].family,
    route: paths.find((path) => path.selected) ?? paths[0]
  }));
};

const FamilyTag: React.FC<{ family: CprEntry['family'] }> = ({ family }) => (
  <GenericTag label={routeFamilyLabel(family)} variant="neutral" size="small" />
);

/** Outbound: one card per neighbor the subnet's CIDR is announced to. */
const OutboundNeighborCard: React.FC<{ entry: CprEntry }> = ({ entry }) => (
  <div className="p-3 bg-semantic-bg-secondary rounded border border-semantic-border-secondary">
    <div className="mb-2">
      <div className="text-lg font-semibold text-semantic-text-primary mb-1">{entry.neighbor_ip}</div>
      <div className="flex items-center justify-end">
        <FamilyTag family={entry.family} />
      </div>
    </div>
    <div className="text-xs text-semantic-text-secondary space-y-1">
      <div>Next Hop: {entry.specific_next_hop || 'N/A'}</div>
      <div>Local Preference: {entry.local_pref} • Origin: {entry.origin || 'N/A'}</div>
      {entry.status && <div>Status: {entry.status}</div>}
    </div>
  </div>
);

/** Inbound: one card per destination, describing the route the table installs for it. */
const InboundPrefixCard: React.FC<{ entry: InboundPrefix }> = ({ entry }) => (
  <div className="p-3 bg-semantic-bg-secondary rounded border border-semantic-border-secondary">
    <div className="flex items-center justify-between mb-2">
      <span className="font-medium text-semantic-text-primary">{entry.prefix}</span>
      <FamilyTag family={entry.family} />
    </div>
    <div className="text-xs text-semantic-text-secondary space-y-1">
      <div>Neighbor IP: {entry.route.neighbor_ip}</div>
      <div>Local Preference: {entry.route.local_pref} • MED: {entry.route.med}</div>
      <div>Specific Next Hop: {entry.route.specific_next_hop || 'N/A'}</div>
      <div>AS_PATH: [{entry.route.as_path?.join(', ') || 'N/A'}]</div>
    </div>
  </div>
);

const RouteTableModal: React.FC<RouteTableModalProps> = ({ isOpen, onClose, routeTableData, vpcId, direction = 'INBOUND' }) => {
  const isLoading = isOpen && !routeTableData;

  const isOutbound = direction === 'OUTBOUND';
  const tagLabels = routeTableData ? formatTagAttribution(findTagAttribution(routeTableData.prefixes)) : [];
  // Inbound reports DISTINCT PREFIXES — one installed route per destination, matching the toolbar
  // and the edge badges; outbound reports ANNOUNCEMENTS, matching its own per-neighbor list below
  // (CTX-322).
  const inboundRoutes = routeTableData ? groupInboundPrefixes(routeTableData.prefixes) : [];
  const headerCount = isOutbound ? (routeTableData?.prefixes.length ?? 0) : inboundRoutes.length;
  const title = routeTableData
    ? (isOutbound ? `Subnet: ${routeTableData.route_table_id}` : `Route Table: ${routeTableData.route_table_id}`)
    : (isOutbound ? 'Subnet' : 'Route Table');
  const sectionTitle = isOutbound ? 'Subnet Information' : 'Route Table Information';
  const idLabel = isOutbound ? 'Subnet ID:' : 'Route Table ID:';
  const countLabel = isOutbound ? 'Announced:' : 'Prefixes:';
  const listTitle = isOutbound ? 'Neighbors' : 'Prefixes';

  return (
    <GenericModal isOpen={isOpen} onClose={onClose} size="lg" title={title}>
      {isLoading ? (
        <div className="space-y-4" role="status" aria-busy="true">
          <div className="space-y-2">
            <SkeletonLine w="45%" h={16} className="rounded" />
            <SkeletonLine w="100%" h={14} className="rounded" />
            <SkeletonLine w="90%" h={14} className="rounded" />
          </div>
          <div className="space-y-2">
            <SkeletonLine w="25%" h={16} className="rounded" />
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="p-3 rounded border border-semantic-border-secondary space-y-2">
                <SkeletonLine w="55%" h={14} className="rounded" />
                <SkeletonLine w="100%" h={12} className="rounded" />
                <SkeletonLine w="80%" h={12} className="rounded" />
              </div>
            ))}
          </div>
        </div>
      ) : !routeTableData ? null : (
      <div className="space-y-4">
        <div>
          <h3 className="font-semibold text-semantic-text-primary mb-2">{sectionTitle}</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-semantic-text-secondary">{idLabel}</span>
              <span className="text-semantic-text-primary font-medium font-mono">{routeTableData.route_table_id}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-semantic-text-secondary">VPC ID:</span>
              <span className="text-semantic-text-primary font-medium">{vpcId}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-semantic-text-secondary">{countLabel}</span>
              <span className="text-semantic-text-primary font-medium">{headerCount}</span>
            </div>
            {/* Only rendered when the payload actually carries a tag — a destination pinned by
                id, or a document from before the field existed, shows no row at all. */}
            {tagLabels.length > 0 && (
              <div className="flex justify-between items-start gap-2">
                <span className="text-semantic-text-secondary">Tags:</span>
                {/* Outbound rules can match on several tags at once, so this is a list. */}
                <div className="flex flex-wrap justify-end gap-1">
                  {tagLabels.map((label) => (
                    <GenericTag key={label} label={label} variant="default" size="small" />
                  ))}
                </div>
              </div>
            )}
            {isOutbound && routeTableData.prefixes.length > 0 && (
              <div className="flex justify-between">
                <span className="text-semantic-text-secondary">Prefix / CIDR Announcements:</span>
                <span className="text-semantic-text-primary font-medium">{routeTableData.prefixes[0].prefix}</span>
              </div>
            )}
          </div>
        </div>
        
        <div>
          <h3 className="font-semibold text-semantic-text-primary mb-2">{listTitle}</h3>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {isOutbound
              ? routeTableData.prefixes.map((entry: CprEntry, index: number) => (
                  <OutboundNeighborCard key={`${entry.neighbor_ip}|${index}`} entry={entry} />
                ))
              : inboundRoutes.map((entry) => <InboundPrefixCard key={entry.key} entry={entry} />)}
          </div>
        </div>
      </div>
      )}
    </GenericModal>
  );
};

export default RouteTableModal;
